import { BusinessOutlined, ContentCopy } from "@mui/icons-material";
import { Button, Card, Divider, Stack, Typography } from "@mui/material";
import CircularLoading from "components/CircularLoading";
import ROUTES from "constants/routes";
import { ToastContext, ToastTypeEnum } from "context/toastContext";
import { UserContext } from "context/userContext";
import { removeAllCookies } from "helpers/cookieHelper";
import { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const NoCompanyPage = () => {
  const navigate = useNavigate();
  const { addToast } = useContext(ToastContext);
  const { me, meError, meLoading, userCompanies, userCompaniesLoading } = useContext(UserContext);

  useEffect(() => {
    if (userCompaniesLoading) return;

    if (userCompanies?.length) {
      navigate(`/${ROUTES.SELECT_COMPANY}`, { replace: true });
    }
  }, [navigate, userCompanies, userCompaniesLoading]);

  const email = me?.userEmail.email || "";

  const handleCopyEmail = () => {
    if (!email) return;

    navigator.clipboard
      .writeText(email)
      .then(() => addToast({ type: ToastTypeEnum.SUCCESS, text: "Почта скопирована" }));
  };

  const handleNavigateCompanyRegister = () => navigate(`/${ROUTES.COMPANY_REGISTER}`);
  const handleNavigateLogin = () => {
    removeAllCookies();
    navigate(`/${ROUTES.LOGIN}`);
  };

  if (meLoading || userCompaniesLoading) {
    return <CircularLoading />;
  }

  if (!me || meError) {
    return (
      <Card
        sx={{
          width: "100%",
          maxWidth: 604,
          mx: "auto",
          px: { xs: 3, sm: 5 },
          py: { xs: 4, sm: 5 },
          borderRadius: 4,
          boxShadow: "0 24px 64px rgba(37, 74, 196, 0.14)",
        }}>
        <Stack spacing={2}>
          <Typography
            variant='h28'
            sx={{ color: "base.700" }}>
            Нет доступа к профилю
          </Typography>
          <Typography
            variant='body16rg'
            sx={{ color: "text.secondary", lineHeight: 1.55 }}>
            Не удалось получить данные пользователя. Выполните вход заново.
          </Typography>
          <Button
            variant='contained'
            size='large'
            onClick={handleNavigateLogin}>
            На страницу входа
          </Button>
        </Stack>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        width: "100%",
        maxWidth: 604,
        mx: "auto",
        px: { xs: 3, sm: 5 },
        py: { xs: 4, sm: 5 },
        borderRadius: 4,
        boxShadow: "0 24px 64px rgba(37, 74, 196, 0.14)",
      }}>
      <Stack spacing={4}>
        <Stack spacing={1.5}>
          <Stack spacing={1.5}>
            <Stack
              sx={{
                width: 92,
                height: 92,
                borderRadius: 3,
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#FFF7E8",
              }}>
              <BusinessOutlined sx={{ fontSize: 40, color: "#E07B00" }} />
            </Stack>

            <Stack spacing={1.5}>
              <Typography
                variant='h28'
                sx={{ color: "#0D2758", fontWeight: 700 }}>
                Вы пока не в компании
              </Typography>

              <Typography
                variant='body16rg'
                sx={{
                  color: "#35507E",
                  fontSize: 18,
                  lineHeight: 1.6,
                }}>
                Чтобы начать работу, получите доступ к пространству компании или создайте своё.
              </Typography>
            </Stack>
          </Stack>

          <Stack
            spacing={2}
            sx={{
              p: 2.5,
              borderRadius: 2.5,
              border: "1px solid #B9C8FF",
              backgroundColor: "#EEF3FF",
            }}>
            <Typography
              variant='h20'
              sx={{ color: "#2147AF", fontWeight: 700 }}>
              Попросите руководителя выдать доступ
            </Typography>

            <Typography
              variant='body16rg'
              sx={{
                color: "#2147AF",
                fontSize: 17,
                lineHeight: 1.55,
              }}>
              Отправьте эту почту руководителю — он откроет вам доступ к пространству компании.
            </Typography>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}>
            <Stack
              sx={{
                flex: 1,
                minHeight: 48,
                px: 2.5,
                borderRadius: 2,
                border: "1px solid #D5E0FF",
                backgroundColor: "#F8FAFF",
                justifyContent: "center",
              }}>
              <Typography
                variant='h20'
                sx={{
                  color: "#0D2758",
                  fontWeight: 600,
                  wordBreak: "break-word",
                }}>
                {email}
              </Typography>
            </Stack>

            <Button
              variant='outlined'
              size='small'
              startIcon={<ContentCopy />}
              onClick={handleCopyEmail}
              sx={{
                minWidth: { xs: "100%", sm: 168 },
                borderColor: "#B9C8FF",
                color: "primary.main",
                backgroundColor: "common.white",
                borderRadius: 2,
              }}>
              Скопировать
            </Button>
          </Stack>

          <Divider />

          <Stack spacing={1.5}>
            <Typography
              variant='body16rg'
              sx={{ color: "#95A7CF", fontSize: 18 }}>
              Или создайте собственную компанию
            </Typography>

            <Button
              variant='outlined'
              size='small'
              onClick={handleNavigateCompanyRegister}
              sx={{
                minHeight: 48,
                borderColor: "#B9C8FF",
                color: "primary.main",
                fontSize: 18,
                fontWeight: 600,
                backgroundColor: "common.white",
                borderRadius: 2,
              }}>
              Регистрация новой компании
            </Button>

            <Typography
              variant='body16rg'
              sx={{
                color: "#A1AFD1",
                lineHeight: 1.55,
                textAlign: "center",
                mx: "auto",
              }}>
              Как только руководитель выдаст доступ, компания появится здесь при следующем входе.
            </Typography>
          </Stack>
        </Stack>
        <Button
          variant='text'
          size='small'
          onClick={handleNavigateLogin}
          sx={{
            alignSelf: "center",
            color: "primary.main",
            fontSize: 16,
            fontWeight: 600,
          }}>
          На страницу входа
        </Button>
      </Stack>
    </Card>
  );
};

export default NoCompanyPage;
